import { ContactsGeneralSearch } from "@/components/features/contact/ContactsGeneralSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  AddContactSlideover,
  type createContactFormSchema,
} from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import ContactsSearchBar from "@/components/features/contact/ContactsSearchBar/ContactsSearchBar";
import { ContactsTable } from "@/components/features/contact/ContactsTable/ContactsTable";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { Button, Form, Switch, Typography } from "@/components/ui";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { useUser } from "@/components/UserProvider";
import { useSearchObject } from "@/hooks/useSearchObject";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Contact } from "@/lib/types";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { PlusCircle } from "lucide-react";

interface SelectContactModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelectContact: (contact: Contact) => void | Promise<void>;
  onSelectContacts?: (contacts: Contact[]) => void | Promise<void>;
  multi?: boolean;
  excludeContactIds?: string[];
  title?: string;
  description?: string;
  label?: string;
  createNewContactLabel?: string;
  defaultSearchObject?: { [key: string]: string[] };
  defaultSearchField?: string;
  defaultValues?: Partial<z.infer<typeof createContactFormSchema>>;
  contactPickerMode?: "inline" | "modal";
  matterId?: string;
}

type SelectContactFormValues = {
  selectedContact?: Contact;
};

export default function SelectContactModal({
  open,
  setOpen,
  onSelectContact,
  onSelectContacts,
  multi = false,
  excludeContactIds,
  title = "Select contact",
  description,
  label = "Contact",
  createNewContactLabel,
  defaultSearchObject,
  defaultSearchField,
  defaultValues,
  contactPickerMode = "inline",
  matterId,
}: SelectContactModalProps) {
  const { hasActionPermission, hasPagePermission } = useUser();
  const canSearchContactDirectory = hasPagePermission(
    PAGE_PERMISSION_KEYS.contactsAll,
  );
  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
  } = useSearchObject({ defaultSearchObject });
  const isInlineContactPicker = contactPickerMode === "inline";
  const [isSelectingContact, setIsSelectingContact] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [isCreateContactSlideoverOpen, setIsCreateContactSlideoverOpen] =
    useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [showOnlyMatterContacts, setShowOnlyMatterContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const debouncedContactSearch = useDebouncedValue(contactSearch, 250);
  const isMultiSelect = multi || Boolean(onSelectContacts);
  const form = useForm<SelectContactFormValues>({
    defaultValues: {
      selectedContact: undefined,
    },
  });

  useEffect(() => {
    if (!open) {
      setContactSearch("");
      setIsSelectingContact(false);
      setIsCreatingContact(false);
      setIsCreateContactSlideoverOpen(false);
      setSelectedContacts([]);
      setShowOnlyMatterContacts(false);
      form.reset({
        selectedContact: undefined,
      });
    }
  }, [form, open]);

  const toggleSelectedContact = (contact: Contact) => {
    setSelectedContacts((current) =>
      current.some((selected) => selected.id === contact.id)
        ? current.filter((selected) => selected.id !== contact.id)
        : [...current, contact],
    );
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      size="2xl"
      className={
        isMultiSelect
          ? "h-[min(80vh,780px)] max-w-2xl"
          : isCreatingContact
            ? "h-[min(92vh,980px)] max-w-[min(96vw,1440px)]"
            : "h-[min(80vh,780px)] max-w-2xl"
      }
      bodyClassName="px-2 pt-3 pb-2"
      bodyContained
      loading={isSelectingContact}
      footer={
        isMultiSelect ? (
          <Button
            type="button"
            className="w-full"
            disabled={selectedContacts.length === 0}
            onClick={() => {
              void onSelectContacts?.(selectedContacts);
              setOpen(false);
            }}
          >
            Add selected ({selectedContacts.length})
          </Button>
        ) : undefined
      }
    >
      <Form {...form}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {isMultiSelect ? (
            <>
              {matterId ? (
                <div className="mb-3 flex shrink-0 items-center gap-3 rounded-md border p-3">
                  <Switch
                    checked={
                      showOnlyMatterContacts || !canSearchContactDirectory
                    }
                    disabled={!canSearchContactDirectory}
                    onCheckedChange={setShowOnlyMatterContacts}
                  />
                  <div className="flex flex-col gap-1">
                    <Typography className="font-medium">
                      Matter connected contacts
                    </Typography>
                    <Typography className="text-muted-foreground text-xs">
                      Only show contacts connected to this matter.
                    </Typography>
                  </div>
                </div>
              ) : null}
              <ContactsTable
                contextMatterId={matterId}
                generalSearch={debouncedContactSearch}
                layout="list"
                scrollContainer="internal"
                multi
                isContactSelected={(contact) =>
                  selectedContacts.some(
                    (selected) => selected.id === contact.id,
                  )
                }
                searchBar={
                  <div className="flex min-w-0 items-center gap-3 py-1">
                    <ContactsGeneralSearch
                      value={contactSearch}
                      onChange={setContactSearch}
                      className="min-w-0 flex-1 sm:w-auto"
                    />
                    <div className="shrink-0">
                      <ContactsSearchBar
                        addSearchObjectValue={addSearchObjectValue}
                        removeSearchObjectValue={removeSearchObjectValue}
                        searchObject={searchObject}
                        defaultSearchField={defaultSearchField ?? "name"}
                        fullWidth
                      />
                    </div>
                  </div>
                }
                excludeContactIds={excludeContactIds}
                onRowClick={(row) => {
                  toggleSelectedContact(row.original);
                }}
                tableName={tanstackTableNames.contactsSlideover}
                searchObject={searchObject}
                onClearFilters={() => {
                  setContactSearch("");
                  setShowOnlyMatterContacts(false);
                  void clearSearchObject();
                }}
                matterIds={
                  (showOnlyMatterContacts || !canSearchContactDirectory) &&
                  matterId
                    ? [matterId]
                    : undefined
                }
                tableButtons={
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.contact,
                    ACTION_PERMISSION_VALUES.create,
                  ) ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsCreateContactSlideoverOpen(true);
                      }}
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span className="ml-2">Create contact</span>
                    </Button>
                  ) : undefined
                }
              />
            </>
          ) : (
            <AddContactFormField
              matterId={matterId}
              label={label}
              register={form.register("selectedContact")}
              inlineContactPicker={isInlineContactPicker}
              parentSlideoverOpen={open}
              autoOpenContactSlideover={!isInlineContactPicker}
              setSelectedContact={(contact) => {
                if (isInlineContactPicker) {
                  return;
                }
                void onSelectContact(contact);
                setOpen(false);
              }}
              excludeContactIds={excludeContactIds}
              defaultSearchObject={defaultSearchObject}
              defaultSearchField={defaultSearchField}
              defaultValues={defaultValues}
              createNewContactLabel={createNewContactLabel}
              showEmbeddedCreateSuccessToast={false}
              hideInlineCreateContactAction={
                !isInlineContactPicker ||
                !hasActionPermission(
                  ACTION_PERMISSION_KEYS.contact,
                  ACTION_PERMISSION_VALUES.create,
                )
              }
              onContactSelectedForSubmit={async (contact) => {
                try {
                  await onSelectContact(contact);
                  setOpen(false);
                } finally {
                  setIsSelectingContact(false);
                }
              }}
              onInlineContactPickInitiated={() => {
                setIsSelectingContact(true);
              }}
              onInlineEmbeddedCreateStateChange={({ active }) => {
                setIsCreatingContact(active);
              }}
              className="flex min-w-0 flex-col"
            />
          )}
        </div>
      </Form>
      {isMultiSelect ? (
        <AddContactSlideover
          open={isCreateContactSlideoverOpen}
          setOpen={setIsCreateContactSlideoverOpen}
          afterCreateFn={(response) => {
            const contact = response.data;
            setSelectedContacts((current) => [...current, contact]);
          }}
        />
      ) : null}
    </AppModal>
  );
}
